// Permission utility functions for frontend
import { apiGet } from './apiClient';
import { useState, useEffect } from 'react';

// Cache for user permissions
let userPermissionsCache = null;
let permissionsCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get auth token from localStorage or sessionStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('session_token') || 
         localStorage.getItem('authToken') || 
         sessionStorage.getItem('authToken');
};

/**
 * Get auth headers
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
    'Accept': 'application/json'
  };
};

/**
 * Fetch user permissions from API or localStorage
 */
export const fetchUserPermissions = async (forceRefresh = false) => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.warn('No auth token found');
      return getPermissionsFromLocalStorage();
    }

    // Use full URL to ensure proper fetch
    const apiUrl = `https://newerp.awasthivitality.com/api/user/permissions?token=${encodeURIComponent(token)}`;
    console.log('Fetching permissions from:', apiUrl);
    
    const response = await fetch(apiUrl, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Session-Token': token
      }
    });
    
    const data = await response.json();
    console.log('Permissions API response:', data);
    
    if (data.success && data.permissions) {
      userPermissionsCache = data.permissions;
      permissionsCacheTime = Date.now();
      console.log('✓ Permissions fetched from API:', data.permissions);
      console.log('Operation permissions:', data.permissions?.operation);
      return data.permissions;
    } else {
      console.error('Failed to fetch permissions from API:', data.message);
      if (data.code === 'UNAUTHORIZED') {
        console.warn('User not authenticated - returning null');
        return null;
      }
      // Don't fallback to localStorage if we have a token - it might be stale
      return null;
    }
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    // Don't fallback to localStorage if we have a token - it might be stale
    return null;
  }
};

/**
 * Get permissions from localStorage (fallback)
 */
const getPermissionsFromLocalStorage = () => {
  try {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      const user = JSON.parse(userData);
      if (user && user.permission) {
        console.log('✓ Using permissions from localStorage:', user.permission);
        return user.permission;
      }
    }
    console.warn('No permissions found in localStorage');
    return null;
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error);
    return null;
  }
};

/**
 * Get user permissions (from cache or API)
 */
export const getUserPermissions = async (forceRefresh = false) => {
  // If force refresh, clear cache and fetch fresh
  if (forceRefresh) {
    userPermissionsCache = null;
    permissionsCacheTime = null;
  }
  
  // Return cached permissions if still valid and not forcing refresh
  if (!forceRefresh && userPermissionsCache && permissionsCacheTime && 
      (Date.now() - permissionsCacheTime) < CACHE_DURATION) {
    console.log('Using cached permissions');
    return userPermissionsCache;
  }
  
  // Fetch fresh permissions
  console.log('Fetching fresh permissions from API');
  return await fetchUserPermissions(forceRefresh);
};

/**
 * Clear permissions cache (call this after login/logout)
 */
export const clearPermissionsCache = () => {
  userPermissionsCache = null;
  permissionsCacheTime = null;
  console.log('✓ Permissions cache cleared');
};

/**
 * Check if user has specific permission
 * @param {string} category - Permission category (master, operation, report, profile, delivery)
 * @param {string} permission - Specific permission name
 * @returns {boolean}
 */
export const hasPermission = async (category, permission) => {
  try {
    const permissions = await getUserPermissions();
    
    console.log('Permission check:', { category, permission, permissions });
    
    if (!permissions) {
      console.warn('No permissions available - user may not be authenticated');
      return false;
    }
    
    // Special case: profile permission can be in multiple categories
    if (category === 'profile' && permission === 'profile') {
      console.log('Checking profile.profile permission in multiple categories...');
      
      // Check profile category (current database structure)
      if (permissions.profile && permissions.profile.includes('profile')) {
        console.log('✅ Found in profile category:', permissions.profile);
        return true;
      }
      
      // Check company category (original Awsthi)
      if (permissions.company && permissions.company.includes('profile')) {
        console.log('✅ Found in company category:', permissions.company);
        return true;
      }
      
      // Check master category (alternative)
      if (permissions.master && permissions.master.includes('profile')) {
        console.log('✅ Found in master category:', permissions.master);
        return true;
      }
      
      console.error('❌ Profile permission NOT found in any category!');
      console.log('Available categories:', Object.keys(permissions));
      return false;
    }

    // Cross-category support: delivery permissions may be stored under 'delivery' or 'operation'
    const categoryLower = (category || '').toLowerCase();
    const isDeliveryLike = /delivery|courier/i.test(permission || '');
    const candidateCategories = new Set([categoryLower]);
    if (isDeliveryLike) {
      candidateCategories.add('delivery');
      candidateCategories.add('operation');
    }

    // Build alias list for permission (to tolerate backend naming differences)
    const buildAliases = (perm) => {
      const p = (perm || '').toLowerCase();
      const aliases = new Set([p]);
      // Common delivery/courier synonyms
      const add = (s) => aliases.add(s);
      if (p.includes('delivery')) {
        add(p.replace('delivery', 'deliveries'));
        add(p.replace('deliveries', 'delivery'));
      }
      if (p.includes('courierentry')) {
        add(p.replace('courierentry', 'delivery'));
        add(p.replace('courierentry', 'deliveries'));
        add(p.replace('courierentry', 'courier'));
      }
      if (p.includes('courier')) {
        add(p.replace('courier', 'courierentry'));
        add(p.replace('courier', 'delivery'));
        add(p.replace('courier', 'deliveries'));
      }
      if (p.endsWith('list')) {
        add(p.replace('list', '')); // e.g., courierentrylist -> courierentry
      } else {
        add(`${p}list`); // e.g., delivery -> deliverylist
      }
      if (p.startsWith('add')) {
        add(p.replace(/^add/, 'create'));
      }
      if (p.startsWith('create')) {
        add(p.replace(/^create/, 'add'));
      }
      // Return array
      return Array.from(aliases);
    };

    const permissionAliases = buildAliases(permission);

    // Try each candidate category
    for (const cat of candidateCategories) {
      // Find category (case-insensitive) in returned permissions
      let catPerms = null;
      let actualCategory = null;
      for (const key of Object.keys(permissions)) {
        if (key.toLowerCase() === cat) {
          catPerms = permissions[key];
          actualCategory = key;
          break;
        }
      }
      if (!catPerms) continue;

      // Case 1: Direct key access with boolean/truthy value (object map)
      if (typeof catPerms === 'object' && catPerms !== null && !Array.isArray(catPerms)) {
        for (const key of Object.keys(catPerms)) {
          const keyLower = key.toLowerCase();
          if (permissionAliases.includes(keyLower) && catPerms[key]) {
            console.log('Permission result (direct key):', { category: actualCategory, permission, matched: keyLower, hasAccess: true, value: catPerms[key] });
            return true;
          }
        }
        // Nested arrays
        for (const key of Object.keys(catPerms)) {
          const sub = catPerms[key];
          if (Array.isArray(sub)) {
            const found = sub.some(p => p && permissionAliases.includes(p.toString().toLowerCase()));
            if (found) {
              console.log('Permission result (nested):', { category: actualCategory, key, permission, hasAccess: true });
              return true;
            }
          }
        }
      }

      // Case 2: Flat array under category
      if (Array.isArray(catPerms)) {
        const hasAccessFlat = catPerms.some(p => {
          if (!p) return false;
          const permStr = p.toString().toLowerCase();
          return permissionAliases.includes(permStr);
        });
        console.log('Permission result (flat array):', { 
          category: actualCategory, 
          permission, 
          hasAccess: hasAccessFlat,
          searchedIn: catPerms,
          permissionAliases
        });
        if (hasAccessFlat) return true;
      }
    }

    console.log('Permission result:', { category, permission, hasAccess: false });
    return false;
  } catch (error) {
    console.error('Permission check error:', error);
    // Return false on error to enforce security
    return false;
  }
};

/**
 * Check if user has any of the specified permissions
 * @param {Array} permissionChecks - Array of {category, permission} objects
 * @returns {boolean}
 */
export const hasAnyPermission = async (permissionChecks) => {
  for (const check of permissionChecks) {
    if (await hasPermission(check.category, check.permission)) {
      return true;
    }
  }
  return false;
};

/**
 * Check if user has all of the specified permissions
 * @param {Array} permissionChecks - Array of {category, permission} objects
 * @returns {boolean}
 */
export const hasAllPermissions = async (permissionChecks) => {
  for (const check of permissionChecks) {
    if (!(await hasPermission(check.category, check.permission))) {
      return false;
    }
  }
  return true;
};

/**
 * Clear permission cache (useful for logout)
 */
export const clearPermissionCache = () => {
  userPermissionsCache = null;
  permissionsCacheTime = null;
};

/**
 * Permission constants for easy reference
 */
export const PERMISSIONS = {
  // PROFILE
  PROFILE: {
    COMPANY: 'profile.profile'
  },
  
  // MASTERS
  MASTER: {
    USER: 'master.user',
    ADD_USER: 'master.adduser',
    EDIT_USER: 'master.edituser',
    COURIER: 'master.courier',
    ADD_COURIER: 'master.addcourier',
    EDIT_COURIER: 'master.editcourier',
    DIAGNOSIS: 'master.diagnosis',
    ADD_DIAGNOSIS: 'master.adddiagnosis',
    EDIT_DIAGNOSIS: 'master.editdiagnosis',
    DELETE_DIAGNOSIS: 'master.deletediagnosis',
    COURSE: 'master.course',
    ADD_COURSE: 'master.addcourse',
    EDIT_COURSE: 'master.editcourse',
    DELETE_COURSE: 'master.deletecourse',
    NOTIFICATION: 'master.notification',
    ADD_NOTIFICATION: 'master.addnotification',
    EDIT_NOTIFICATION: 'master.editnotification',
    DELETE_NOTIFICATION: 'master.deletenotification',
    TESTIMONIAL: 'master.testimonial',
    ADD_TESTIMONIAL: 'master.addtestimonial',
    EDIT_TESTIMONIAL: 'master.edittestimonial',
    BLOG: 'master.blog',
    ADD_BLOG: 'master.addblog',
    EDIT_BLOG: 'master.editblog',
    PRODUCT: 'master.product',
    ADD_PRODUCT: 'master.addproduct',
    EDIT_PRODUCT: 'master.editproduct',
    CATEGORY: 'master.category',
    ADD_CATEGORY: 'master.addcategory',
    EDIT_CATEGORY: 'master.editcategory',
    VIDEO: 'master.video',
    ADD_VIDEO: 'master.addvideo',
    EDIT_VIDEO: 'master.editvideo',
    DELETE_VIDEO: 'master.deletevideo'
  },
  
  // OPERATION
  OPERATION: {
    REGISTERED_PATIENT: 'operation.registered_patient',
    APPOINTMENT: 'operation.appointment',
    PATIENT: 'operation.patient',
    EDIT_PATIENT: 'operation.editpatient',
    DELETE_PATIENT: 'operation.deletepatient',
    REORDER: 'operation.reorder',
    PATIENT_ENTRY: 'operation.patiententry',
    ADD_PATIENT_ENTRY: 'operation.addpatiententry',
    EDIT_PATIENT_ENTRY: 'operation.editpatiententry',
    DELETE_PATIENT_ENTRY: 'operation.deletepatiententry',
    COURIER_ENTRY: 'operation.courierentry',
    ADD_COURIER_ENTRY: 'operation.addcourierentry',
    EDIT_COURIER_ENTRY: 'operation.editcourierentry',
    // Delivery aliases
    DELIVERY: 'operation.delivery',
    DELIVERIES: 'operation.deliveries',
    ADD_DELIVERY: 'operation.adddelivery',
    EDIT_DELIVERY: 'operation.editdelivery',
    DELETE_DELIVERY: 'operation.deletedelivery',
    COURIER_ENTRY_LIST: 'operation.courierentrylist'
  },
  
  // REPORTS
  REPORT: {
    PATIENT: 'report.patient',
    COURIER: 'report.courier'
  }
};

/**
 * React hook for permission checking
 */
export const usePermission = (category, permission) => {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const checkPermission = async () => {
      setLoading(true);
      const access = await hasPermission(category, permission);
      setHasAccess(access);
      setLoading(false);
    };
    
    checkPermission();
  }, [category, permission]);
  
  return { hasAccess, loading };
};
